import {
  BATTLE_REEL_TIMINGS,
  type Battle,
  type BattleCopy,
  type BattleRoomCopy,
  capitalizeTerm,
  defaultBattleCopy,
  defaultBattleRoomCopy,
} from "@loot-os/api-contract";
import type { BattleRoundBox } from "@loot-os/design-system/domain/battles/battleTypes";
import type {
  BattleRoomContent,
  BattleRoomSeatsContent,
  SeatAction,
} from "@loot-os/design-system/templates/battles/battlesContent";
import { isLootApiErrorLike, useClientSeed } from "@loot-os/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { createElement, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BattleReels } from "../../../../components/battles/BattleReels.js";
import { useBattleMachine } from "../../../../lib/battleMachine.js";
import {
  useAddBot,
  useClaimBattle,
  useJoinBattle,
  useSellBackBattle,
  useStartBattle,
} from "../../../../lib/battles.js";
import {
  canAnyoneStart,
  countdownMsOf,
  liveTimelineRoundIndex,
  resyncTargetRound,
  visualRevealAtMs,
} from "../../../../lib/battleTimeline.js";
import { useCurrency } from "../../../../lib/currency.js";
import { useProfile } from "../../../../lib/profile.js";
import { battleQueryOptions, qk } from "../../../../lib/queries.js";
import { queryClient } from "../../../../lib/queryClient.js";
import { pendingRevealFromError, useRevealClaim } from "../../../../lib/revealClaim.js";
import { serverNow } from "../../../../lib/serverClock.js";
import { useShopper } from "../../../../lib/shopper.js";
import { useTerminology } from "../../../../lib/terminology.js";
import { useBattleChannel } from "../../../../lib/useBattleChannel.js";
import { battleTags, toSeatView } from "../shared/battleViews.js";
import { useBattleFairnessContent } from "./content/fairness.js";
import { battleOutcome } from "./content/outcome.js";
import { resultLanes } from "./content/results.js";

const NO_LANES: ReadonlySet<number> = new Set();

/**
 * One battle: lobby → countdown → live rounds → results. Server data is canonical (polled until
 * the Event API subscription is live, pushed after); the stage machine decides what shows when,
 * the timeline math when a round may render. The words are the store's default copy
 * (`defaultBattleRoomCopy`, with `defaultBattleCopy` for the seats, tags and rounds); the claim's,
 * the refusals and the load failures are the platform's.
 */
export function useBattleRoomContent(): BattleRoomContent {
  const { battleId } = useParams({ from: "/battles/$battleId" });
  const { replay: replayParam } = useSearch({ from: "/battles/$battleId" });
  const { user, sessionToken, authPending, sessionPending } = useShopper();
  const currency = useCurrency();
  const terms = useTerminology();
  const term = terms.battle;
  const words = defaultBattleCopy(terms);
  const copy = defaultBattleRoomCopy(terms);
  const { showProfile } = useProfile();
  const navigate = useNavigate();
  const live = useBattleChannel(battleId);
  const [clientSeed] = useClientSeed({});

  // Wait for shopper auth to settle before the first fetch: a completed battle stops polling, so a
  // spectator response fetched during the auth → session handoff would cache `me: null` and never
  // refetch, hiding the claim from a seated winner. A signed-out visitor settles immediately.
  const authSettled = !authPending && !sessionPending;
  const query = useQuery({
    ...battleQueryOptions(battleId, { poll: !live }),
    enabled: !!battleId && authSettled,
  });
  const battle = query.data?.battle ?? null;
  const loadError = query.error
    ? query.error instanceof Error && query.error.message
      ? query.error.message
      : `failed to load the ${term.singular}`
    : null;

  // The viewer's Loot end-user id comes from the `me` block, the one place the server names it.
  const viewerEndUserId = battle?.me
    ? (battle.seats[battle.me.seatIndex]?.endUser?.id ?? null)
    : null;
  const isCreator = battle?.me?.seatIndex === 0;

  const snapshot = useMemo(
    () =>
      battle
        ? {
            status: battle.status,
            currentRevealedRound: battle.currentRevealedRound,
            roundCount: battle.roundCount,
            startedAtMs: battle.startedAt ? Date.parse(battle.startedAt) : null,
            startCountdownMs: countdownMsOf(battle),
            revealIntervalMs: battle.revealIntervalMs,
          }
        : null,
    [battle],
  );
  const { machine, state } = useBattleMachine(snapshot, serverNow);

  // Show + hold after the reels land, then move on.
  useEffect(() => {
    if (state.stage !== "live" || state.roundPhase !== "showing" || !battle) return;
    const hold = battle.fastSpin ? BATTLE_REEL_TIMINGS.quickHoldMs : BATTLE_REEL_TIMINGS.holdMs;
    const timer = setTimeout(() => machine.holdFinished(), hold);
    return () => clearTimeout(timer);
  }, [state.stage, state.roundPhase, battle, machine]);

  // A live round may render only once its boundary on the server's clock has passed.
  const roundGateMs =
    battle && state.stage === "live" && !state.replaying
      ? visualRevealAtMs(battle, state.round)
      : null;
  const gateOpen = useGate(roundGateMs);

  // The seats whose reel has landed this round, so the results light their card up as it lands
  // rather than waiting for the last lane.
  const [settled, setSettled] = useState<{ round: number; lanes: ReadonlySet<number> }>({
    round: -1,
    lanes: NO_LANES,
  });
  const settledLanes = settled.round === state.round ? settled.lanes : NO_LANES;
  const onLaneSettled = useCallback(
    (seatIndex: number) => {
      setSettled((prev) => {
        const lanes = new Set(prev.round === state.round ? prev.lanes : []);
        lanes.add(seatIndex);
        return { round: state.round, lanes };
      });
    },
    [state.round],
  );

  // Replay auto-start from ?replay=1, once the battle is completed.
  const replayedRef = useRef(false);
  useEffect(() => {
    if (replayParam && battle?.status === "completed" && !replayedRef.current) {
      replayedRef.current = true;
      machine.replay();
      void navigate({ to: "/battles/$battleId", params: { battleId }, search: {}, replace: true });
    }
  }, [replayParam, battle?.status, machine, navigate, battleId]);

  // After a real tab-out, catch up: refetch and snap to where the timeline is.
  const hiddenRef = useRef(false);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenRef.current = true;
        return;
      }
      if (!hiddenRef.current) return;
      hiddenRef.current = false;
      if (!battle || state.stage !== "live" || state.replaying) return;
      const timelineRound = liveTimelineRoundIndex(battle, serverNow());
      if (timelineRound === null || timelineRound <= state.round) return;
      const key = qk.battle(battleId);
      void queryClient.refetchQueries({ queryKey: key }).then(() => {
        const fresh = queryClient.getQueryData<typeof query.data>(key)?.battle;
        if (!fresh) return;
        machine.snapTo(
          resyncTargetRound({
            currentRevealedRound: fresh.currentRevealedRound,
            timelineRound,
            roundCount: fresh.roundCount,
          }),
        );
      });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [battle, state.stage, state.round, state.replaying, machine, battleId]);

  const join = useJoinBattle(battleId);
  const addBot = useAddBot(battleId);
  const start = useStartBattle(battleId);
  const claim = useClaimBattle(battleId);
  const sellBack = useSellBackBattle(battleId);
  const [actionError, setActionError] = useState<string | null>(null);
  const pending = join.isPending || addBot.isPending || start.isPending;
  const describe = useCallback(
    (err: unknown, fallback: string) => {
      if (isLootApiErrorLike(err)) {
        if (err.code === "insufficient_balance")
          return `Not enough ${currency.namePlural} to enter.`;
        if (err.code === "reveal_pending") return "Claim your pending reveal first.";
        if (err.code === "battle_seat_taken") return "That seat was just taken.";
        if (err.code === "battle_not_joinable") return `This ${term.singular} has already started.`;
        if (err.code === "battles_disabled")
          return `${capitalizeTerm(term.plural)} are switched off for this store.`;
        return err.message || fallback;
      }
      return err instanceof Error ? err.message : fallback;
    },
    [currency.namePlural, term],
  );

  const { promptClaim } = useRevealClaim();
  // A blocked action means an unclaimed reveal: open the claim prompt instead of a dead end.
  const handleActionError = useCallback(
    (err: unknown, fallback: string) => {
      const hint = pendingRevealFromError(err);
      if (hint) {
        setActionError(null);
        promptClaim(hint);
        return;
      }
      setActionError(describe(err, fallback));
    },
    [describe, promptClaim],
  );

  const boxMap = useMemo(() => {
    const map = new Map<string, BattleRoundBox>();
    for (const version of battle?.boxVersions ?? []) {
      map.set(version.boxId, { name: version.name, imageUrl: version.imageUrl });
    }
    return map;
  }, [battle?.boxVersions]);

  const onRoundAnimationFinished = useCallback(() => machine.roundAnimationFinished(), [machine]);
  const onCountdownFinished = useCallback(() => machine.countdownFinished(), [machine]);
  const fairness = useBattleFairnessContent(battle, copy.fairness);

  const back = { label: words.lobby, href: "/battles" };
  if (!battle) {
    return {
      header: {
        back,
        state: loadError ? { status: "error", message: loadError } : { status: "loading" },
      },
    };
  }

  // The last round every seat shows in the results, and the round whose landed seats show too.
  const shownThrough =
    state.stage === "completed"
      ? battle.roundCount - 1
      : state.stage === "live"
        ? state.roundPhase === "showing"
          ? state.round
          : state.round - 1
        : -1;
  const currentRound = state.stage === "live" ? state.round : -1;
  const displayRound =
    state.stage === "completed"
      ? battle.roundCount
      : state.stage === "live"
        ? state.round + 1
        : state.stage === "countdown"
          ? 1
          : 0;
  const countdownEndsAt = state.replaying
    ? (state.replayStartedAtMs ?? serverNow()) + countdownMsOf(battle)
    : battle.startedAt
      ? Date.parse(battle.startedAt) + countdownMsOf(battle)
      : null;
  const stageRound = state.stage === "countdown" ? 0 : state.round;
  const stageVersion = battle.boxVersions.find(
    (v) => v.id === battle.rounds[stageRound]?.boxVersionId,
  );

  return {
    header: {
      back,
      state: {
        status: "ready",
        title: copy.header.title(battle.playerCount),
        tags: battleTags(battle, words),
        rounds: {
          label: copy.header.rounds,
          current: displayRound,
          total: battle.roundCount,
          boxes: battle.rounds.map(
            (round) => boxMap.get(round.boxId) ?? { name: words.unknownBox, imageUrl: "" },
          ),
          active: state.stage === "live" ? state.round : state.stage === "countdown" ? 0 : -1,
          roundLabel: words.roundBox,
        },
        entry: { label: copy.header.entry, cents: battle.priceCents },
        copyLink: {
          label: copy.header.copyLink,
          onCopy: () => void navigator.clipboard?.writeText(window.location.href),
        },
      },
    },
    notice: actionError ? { message: actionError } : null,
    seats:
      state.stage === "waiting"
        ? waitingSeats({
            battle,
            viewerEndUserId,
            showProfile,
            words,
            copy: copy.seats,
            signedIn: !!user && !!sessionToken,
            isCreator,
            canStartAsStranger: canAnyoneStart(battle, serverNow()),
            pending,
            onJoin: (seatIndex) => {
              setActionError(null);
              join.mutate(
                { seatIndex, clientSeed },
                { onError: (err) => handleActionError(err, "could not join") },
              );
            },
            onAddBot: (seatIndex) => {
              setActionError(null);
              addBot.mutate(
                { seatIndex },
                { onError: (err) => handleActionError(err, "could not add a bot") },
              );
            },
            onStart: () => {
              setActionError(null);
              start.mutate(undefined, {
                onError: (err) => handleActionError(err, "could not start"),
              });
            },
          })
        : null,
    stage:
      state.stage === "countdown" || state.stage === "live"
        ? {
            roundLabel: copy.stage.round(
              stageRound + 1,
              battle.roundCount,
              stageVersion?.name ?? null,
            ),
            replayLabel: state.replaying ? copy.stage.replay : undefined,
            reels: createElement(BattleReels, {
              battle,
              state,
              viewerEndUserId,
              countdownEndsAtMs: countdownEndsAt,
              gateOpen: gateOpen || state.replaying,
              onCountdownFinished,
              onLaneSettled,
              onRoundAnimationFinished,
            }),
          }
        : null,
    outcome:
      state.stage === "completed"
        ? battleOutcome({
            battle,
            viewerEndUserId,
            showProfile,
            currency,
            terms,
            words,
            copy: copy.outcome,
            claim: {
              pending: claim.isPending,
              error: claim.error ? describe(claim.error, "could not claim") : null,
              onClaim: () => claim.mutate(),
            },
            sellBack: {
              pending: sellBack.isPending,
              error: sellBack.error ? describe(sellBack.error, "could not sell back") : null,
              onSellBack: () => sellBack.mutate(),
            },
            onReplay: () => machine.replay(),
          })
        : null,
    results:
      state.stage !== "waiting"
        ? {
            label: copy.results.label,
            lanes: resultLanes({
              battle,
              throughRound: shownThrough,
              currentRound,
              settledLanes,
              viewerEndUserId,
              showProfile,
              words,
            }),
            roundCount: battle.roundCount,
            roundLabel: copy.results.placeholder,
            winnerLabel: copy.results.winner,
          }
        : null,
    fairness,
  };
}

/**
 * The waiting stage: every seat. An empty seat offers Join to a signed-in shopper who holds no
 * seat (Sign in to join signed out), and Add bot to the creator; the footer offers Start now to
 * the creator and Start to anyone signed in once the lobby is stale.
 */
function waitingSeats({
  battle,
  viewerEndUserId,
  showProfile,
  words,
  copy,
  signedIn,
  isCreator,
  canStartAsStranger,
  pending,
  onJoin,
  onAddBot,
  onStart,
}: {
  battle: Battle;
  viewerEndUserId: string | null;
  showProfile: (endUserId: string) => void;
  words: BattleCopy;
  copy: BattleRoomCopy["seats"];
  signedIn: boolean;
  isCreator: boolean;
  canStartAsStranger: boolean;
  pending: boolean;
  onJoin: (seatIndex: number) => void;
  onAddBot: (seatIndex: number) => void;
  onStart: () => void;
}): BattleRoomSeatsContent {
  const seated = battle.me !== null;
  const open = battle.status === "created";
  const empty = battle.seats.filter((s) => s.kind === "empty").length;
  return {
    seats: battle.seats.map((seat) => {
      const actions: SeatAction[] = [];
      if (seat.kind === "empty" && open) {
        if (!signedIn) {
          actions.push({ label: copy.signInToJoin, href: "/login", emphasis: "primary" });
        } else if (!seated) {
          actions.push({
            label: copy.join,
            onClick: () => onJoin(seat.seatIndex),
            emphasis: "primary",
          });
        }
        if (isCreator) {
          actions.push({
            label: copy.addBot,
            onClick: () => onAddBot(seat.seatIndex),
            emphasis: "secondary",
          });
        }
      }
      return {
        seatLabel: copy.seat(seat.seatIndex),
        seat: toSeatView(seat, viewerEndUserId, showProfile, words),
        actions,
      };
    }),
    pending,
    footer: open
      ? {
          message: copy.waiting(empty),
          start: isCreator
            ? { label: copy.startNow, emphasis: "primary", onStart }
            : canStartAsStranger && signedIn
              ? { label: copy.start, emphasis: "secondary", onStart }
              : null,
        }
      : null,
  };
}

/** True once the server clock has passed `atMs` (immediately when null); re-renders at the instant. */
function useGate(atMs: number | null): boolean {
  const [, tick] = useState(0);
  const open = atMs === null || serverNow() >= atMs;
  useEffect(() => {
    if (open || atMs === null) return;
    const timer = setTimeout(() => tick((n) => n + 1), Math.max(0, atMs - serverNow()) + 5);
    return () => clearTimeout(timer);
  }, [atMs, open]);
  return open;
}
